/**
 * PersonalStats — "Strava for industrialists".
 *
 *  - Sessions = entries del mining ledger clusterizadas (gap > 30 min = new session)
 *  - Valoración @ Jita buy (piso realista para vendedor con volumen)
 *  - Top 5 sesiones (ranked por ISK/min), hero best session
 *  - Weekly summary (por semana ISO) con su mejor sesión
 *  - Streaks: racha actual + racha más larga del período
 *
 * Caveat: ESI mining ledger tiene cap ~30d. Para `days > 30` el resultado
 * viene vacío y se avisa al user.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from '../components/LastUpdated'
import { RefreshButton } from '../components/RefreshButton'
import { LoadingSkeleton } from '../components/LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { StatTile } from '../components/ui'

type Period = '30d' | '60d' | 'current_month'

export function PersonalStats() {
  const [period, setPeriod] = useState<Period>('30d')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const currentMonth = period === 'current_month'
  const days = period === '60d' ? 60 : period === '30d' ? 30 : undefined

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['personal-stats', forceRefetchKey, period],
    queryFn: () => api.personalStats({ days, currentMonth }, force),
  })

  const s = data?.summary
  const streak = data?.streak
  const topSessions = data?.top_sessions ?? []
  const weekly = data?.weekly_summary ?? []

  // Para el aviso del cap de ESI (mining ledger ~30d)
  const ledgerWindowExceeded =
    days !== undefined && days > 30
  const observedEmpty = !isLoading && (s?.total_sessions ?? 0) === 0

  const maxWeeklyValue = useMemo(
    () => Math.max(1, ...weekly.map((w) => w.total_value)),
    [weekly],
  )

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Personal stats</h1>
          <p className="text-eve-muted text-sm">
            Tu "Strava" de mining — top sesiones, semanas y rachas. Calculado
            con precios Jita buy de los ores minados. Cero input del user.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <PeriodToggle value={period} onChange={setPeriod} />
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Hero: top session */}
      {s?.top_session && (
        <div className="card border-eve-accent/40 bg-gradient-to-br from-eve-accent/5 to-transparent">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="stat-label flex items-center gap-2">
                <span className="inline-block w-2 h-2 rounded-full bg-eve-accent" />
                Best session ({periodLabel(period)})
              </div>
              <div className="text-3xl font-bold mt-1 font-mono text-eve-accent">
                {formatISK(s.top_session.total_value, 0)} ISK
              </div>
              <div className="text-sm text-eve-muted mt-1">
                {formatISK(s.top_session.isk_per_min, 0)}/min ·{' '}
                {formatISKShort(s.top_session.isk_per_hour)}/h
              </div>
              <div className="text-xs text-eve-muted mt-2 font-mono">
                {fmtDate(s.top_session.start)} ·{' '}
                {s.top_session.duration_min.toFixed(0)} min
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 flex-1 min-w-[260px]">
              <StatTile
                size="sm"
                label="Total value"
                value={`${formatISKShort(s.total_value)} ISK`}
                sub={`${s.days_active} días activos / ${s.total_days_in_period}`}
              />
              <StatTile
                size="sm"
                label="Sesiones"
                value={String(s.total_sessions)}
                sub={`avg ${s.avg_session_min.toFixed(0)} min`}
              />
              <StatTile
                size="sm"
                tone={(streak?.current_streak_days ?? 0) >= 3 ? 'accent' : 'default'}
                label="Racha actual"
                value={streak ? `${streak.current_streak_days}d` : '0d'}
                sub={
                  streak?.last_mined_day
                    ? `última: ${streak.last_mined_day}`
                    : '—'
                }
              />
              <StatTile
                size="sm"
                tone={(s.longest_streak ?? 0) >= 7 ? 'positive' : 'default'}
                label="Mejor racha"
                value={
                  s.longest_streak > 0 ? `${s.longest_streak}d` : '—'
                }
                sub={s.longest_streak_dates ? `${s.longest_streak_dates.from} → ${s.longest_streak_dates.to}` : '—'}
              />
            </div>
          </div>
        </div>
      )}

      {/* Empty hero state */}
      {!isLoading && (s?.top_session === null || s === undefined) && (
        <div className="card text-center py-10 border-eve-border">
          <div className="text-2xl mb-2">📊</div>
          <div className="text-eve-text font-medium">Sin sesiones en este período</div>
          <div className="text-eve-muted text-sm mt-1">
            Salí a minar algo — la próxima sesión aparece acá.
          </div>
        </div>
      )}

      {/* Weekly summary */}
      {weekly.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold mb-3">Weekly summary</h2>
          <div className="space-y-2">
            {weekly.map((w) => {
              const pct = (w.total_value / maxWeeklyValue) * 100
              const isCurrent =
                new Date(w.week_start).getTime() <= Date.now() &&
                Date.now() <= new Date(w.week_end).getTime() + 86400 * 1000
              return (
                <div
                  key={w.week_start}
                  className={`flex items-center gap-3 py-1 ${
                    isCurrent ? '' : ''
                  }`}
                >
                  <div className="w-24 text-xs text-eve-muted font-mono shrink-0">
                    {shortDate(w.week_start)}
                  </div>
                  <div className="flex-1 h-5 bg-eve-panel/40 rounded relative overflow-hidden border border-eve-border/40">
                    <div
                      className="h-full bg-eve-accent/60"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-28 text-right text-xs font-mono shrink-0">
                    {formatISKShort(w.total_value)} ISK
                  </div>
                  <div className="w-16 text-right text-[10px] text-eve-muted font-mono shrink-0 hidden md:block">
                    {w.total_sessions}ses
                  </div>
                  <div className="w-24 text-right text-[10px] text-eve-muted font-mono shrink-0 hidden md:block">
                    {w.best_session
                      ? `best ${formatISKShort(w.best_session.isk_per_min)}/m`
                      : '—'}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Top 5 sessions */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-lg font-semibold">Top 5 sesiones</h2>
          <span className="text-xs text-eve-muted italic">
            ordenadas por ISK/min · click para ver ore breakdown
          </span>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="rows" rows={6} />
        ) : topSessions.length === 0 ? (
          <div className="text-eve-muted py-8 text-center">
            {observedEmpty
              ? 'No hay sesiones mineras en el período.'
              : 'Sin data para mostrar.'}
          </div>
        ) : (
          <div className="space-y-2">
            {topSessions.map((sess, i) => (
              <SessionRow key={sess.session_id} session={sess} rank={i + 1} />
            ))}
          </div>
        )}
      </div>

      {/* Warnings */}
      {ledgerWindowExceeded && (
        <div className="text-xs text-eve-warn italic">
          ⚠ Pediste {days} días pero el mining ledger de ESI solo guarda los
          últimos ~30 días. Reducí el rango o cambiá a <em>30d</em> para ver
          data real.
        </div>
      )}
      {observedEmpty && !ledgerWindowExceeded && !isLoading && (
        <div className="text-xs text-eve-muted italic">
          El ledger no tiene entries en este período. Probablemente ya pasó
          más de un mes desde tu última sesión.
        </div>
      )}

      <p className="text-xs text-eve-muted">
        Una <em>sesión</em> = entries del mining ledger separadas por menos de
        30 min. Valoración a Jita buy (lo que un buyer pagaría — piso
        realista para tu venta). Streaks cuentan días con al menos 1 entry.
        KV cachea los precios 5min; ESI mining ledger es cap ~30d.
      </p>
    </div>
  )
}

function PeriodToggle({
  value,
  onChange,
}: {
  value: Period
  onChange: (p: Period) => void
}) {
  const opts: Array<{ key: Period; label: string }> = [
    { key: 'current_month', label: 'Month' },
    { key: '30d', label: '30d' },
    { key: '60d', label: '60d' },
  ]
  return (
    <div className="flex gap-1">
      {opts.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
            value === o.key
              ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
              : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function SessionRow({
  session,
  rank,
}: {
  session: {
    session_id: string
    start: string
    end: string
    duration_min: number
    total_qty: number
    total_value: number
    isk_per_min: number
    isk_per_hour: number
    day: string
    ores: Record<
      string,
      { type_id: number; name: string; quantity: number; line_value: number }
    >
  }
  rank: number
}) {
  const ores = Object.values(session.ores).sort(
    (a, b) => b.line_value - a.line_value,
  )
  const maxLine = Math.max(1, ...ores.map((o) => o.line_value))

  return (
    <details
      open={rank === 1}
      className={`border rounded-md bg-eve-bg/40 ${
        rank === 1 ? 'border-eve-accent/40' : 'border-eve-border'
      }`}
    >
      <summary className="cursor-pointer p-3 hover:bg-eve-panel/50 transition-colors">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-8 h-8 rounded-full bg-eve-accent/15 border border-eve-accent/40 flex items-center justify-center text-sm font-bold font-mono text-eve-accent shrink-0">
            #{rank}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">
              {fmtDate(session.start)}
              <span className="text-eve-muted text-xs ml-2 font-mono">
                {session.duration_min.toFixed(0)} min
              </span>
            </div>
            <div className="text-xs text-eve-muted mt-0.5">
              {ores.length} {ores.length === 1 ? 'ore' : 'ores'} ·{' '}
              {session.total_qty.toLocaleString('en-US')} u
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-base font-semibold text-eve-text">
              {formatISK(session.total_value, 0)} ISK
            </div>
            <div className="text-[11px] font-mono text-eve-accent">
              {formatISK(session.isk_per_min, 0)}/min ·{' '}
              {formatISKShort(session.isk_per_hour)}/h
            </div>
          </div>
        </div>
      </summary>

      <div className="border-t border-eve-border px-3 py-3 text-xs space-y-3">
        <div>
          <div className="stat-label mb-2">Ore breakdown</div>
          <div className="space-y-1.5">
            {ores.map((o) => {
              const pct = (o.line_value / maxLine) * 100
              return (
                <div key={o.type_id} className="flex items-center gap-2">
                  <div className="w-32 truncate text-eve-text" title={o.name}>
                    {o.name}
                  </div>
                  <div className="flex-1 h-3 bg-eve-panel/40 rounded overflow-hidden border border-eve-border/40">
                    <div
                      className="h-full bg-eve-accent/50"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <div className="w-20 text-right font-mono">
                    {o.quantity.toLocaleString('en-US')} u
                  </div>
                  <div className="w-20 text-right font-mono text-eve-muted">
                    {formatISKShort(o.line_value)} ISK
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </details>
  )
}

function periodLabel(p: Period): string {
  if (p === 'current_month') return 'este mes'
  if (p === '60d') return '60 días'
  return '30 días'
}

function fmtDate(iso: string): string {
  // "2026-08-21T03:14:15Z" → "21 Aug 03:14"
  const d = new Date(iso)
  const day = d.getUTCDate().toString().padStart(2, '0')
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  const hh = d.getUTCHours().toString().padStart(2, '0')
  const mm = d.getUTCMinutes().toString().padStart(2, '0')
  return `${day} ${month} ${hh}:${mm}`
}

function shortDate(iso: string): string {
  // "2026-08-04" → "04 Aug"
  const d = new Date(iso + 'T00:00:00Z')
  const day = d.getUTCDate().toString().padStart(2, '0')
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })
  return `${day} ${month}`
}
